import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["bulkToggle", "bulkTextarea", "bulkSection", "singleSection", "sortableContainer", "itemInput", "listItemTemplate", "saveSection", "emptyMessage"]
  static values = { preitems: Array }

  connect() {
    this.itemCounter = 0;
    this.initializeExistingItems();
    this.initializeSortable();
    this.updateItemPositions();
    this.toggleEmptyMessage();
  }

  initializeExistingItems() {
    this.sortableContainerTarget.innerHTML = '';
    
    let items = [];

    try {
      items = this.preitemsValue || [];
    } catch (error) {
      console.warn('Error parsing preitems:', error);
      items = [];
    }
    
    if (items.length > 0) {
      items.forEach((item, index) => {
        if (!item._destroy && (item.name || item.id)) {
          this.createListItem(item.name || "", item.id);
        }
      });
    }
  }

  initializeSortable() {
    this.sortable = new Sortable(this.sortableContainerTarget, {
      handle: '.handle',
      animation: 150,
      onEnd: () => {
        this.updateItemPositions();
      }
    });
  }

  toggleBulkMode(event) {
    event.preventDefault();
    
    const isBulkMode = !this.bulkSectionTarget.classList.contains('hidden');
    
    if (isBulkMode) {
      // Switch to single mode
      this.bulkSectionTarget.classList.add('hidden');
      this.singleSectionTarget.classList.remove('hidden');
      this.saveSectionTarget.classList.remove('hidden');
      this.bulkToggleTarget.textContent = "Bulk Add";
      this.bulkTextareaTarget.value = "";
      
      this.toggleEmptyMessage();
    } else {
      // Switch to bulk mode
      this.bulkSectionTarget.classList.remove('hidden');
      this.singleSectionTarget.classList.add('hidden');
      this.saveSectionTarget.classList.add('hidden');
      this.bulkToggleTarget.textContent = "Single Add";
      
      this.hideEmptyMessage();

      setTimeout(() => {
        this.bulkTextareaTarget.focus();
      }, 100);
    }
  }

  bulkConvert(event) {
    event.preventDefault();
    const bulkText = this.bulkTextareaTarget.value.trim();
    
    if (!bulkText) {
      this.createListItem("");
    } else {
      const items = bulkText.split(/\r?\n/).filter(item => item.trim());
      
      items.forEach(itemName => {
        this.createListItem(itemName.trim());
      });
    }
    
    // Switch back to single mode
    this.bulkSectionTarget.classList.add('hidden');
    this.singleSectionTarget.classList.remove('hidden');
    this.saveSectionTarget.classList.remove('hidden');
    this.bulkToggleTarget.textContent = "Bulk Add";
    this.bulkTextareaTarget.value = "";
    
    this.updateItemPositions();
    this.hideEmptyMessage();
  }

  addItem(event) {
    event.preventDefault();
    this.createListItem("");
    this.updateItemPositions();
    this.hideEmptyMessage();
    
    setTimeout(() => {
      const inputs = this.sortableContainerTarget.querySelectorAll('input[type="text"]');
      const lastInput = inputs[inputs.length - 1];
      if (lastInput) {
        lastInput.focus();
      }
    }, 100);
  }

  destroyItem(event) {
    event.preventDefault();
    const listItem = event.target.closest('.list-item');
    const destroyInput = listItem.querySelector('input[name*="[_destroy]"]');
    
    listItem.dataset.destroy = "true";
    destroyInput.value = "true";
    listItem.classList.add('hidden');
    
    this.updateItemPositions();
    this.toggleEmptyMessage();
  }

  createListItem(name = "", id = null) {
    const template = this.hasListItemTemplateTarget ? 
      this.listItemTemplateTarget : 
      document.getElementById('list-item-template');
    
    if (!template) {
      console.error('List item template not found');
      return;
    }
    
    let templateHTML = template.innerHTML;
    
    const position = this.itemCounter + 1;
    const inputId = `list_item_input_index_${this.itemCounter}`;
    
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    templateHTML = templateHTML
      .replace(/__INDEX__/g, this.itemCounter)
      .replace(/__POSITION__/g, position)
      .replace(/__INPUT_ID__/g, inputId)
      .replace(/__NAME__/g, escapeHtml(name || ''))
      .replace(/__ID__/g, id || '');
    
    this.sortableContainerTarget.insertAdjacentHTML('beforeend', templateHTML);
    
    this.itemCounter++;
  }

  updateHiddenInput(event) {
    const input = event.target;
    const listItem = input.closest('.list-item');
    const hiddenNameInput = listItem.querySelector('input[name*="[name]"]');
    
    if (hiddenNameInput) {
      hiddenNameInput.value = input.value;
    }
  }

  updateItemPositions() {
    const visibleItems = this.sortableContainerTarget.querySelectorAll('.list-item[data-destroy="false"]:not(.hidden)');
    
    visibleItems.forEach((item, index) => {
      item.dataset.position = (index + 1).toString();
      const positionInput = item.querySelector('input[name*="[position]"]');
      if (positionInput) {
        positionInput.value = (index + 1).toString();
      }
    });
  }

  toggleEmptyMessage() {
    if (!this.hasEmptyMessageTarget) {
      return;
    }

    const visibleItems = this.sortableContainerTarget.querySelectorAll('.list-item:not([data-destroy="true"]):not(.hidden)');
    
    if (visibleItems.length === 0) {
      this.emptyMessageTarget.classList.remove('hidden');
      this.emptyMessageTarget.style.display = 'block';
    } else {
      this.emptyMessageTarget.classList.add('hidden');
      this.emptyMessageTarget.style.display = 'none';
    }
  }

  hideEmptyMessage() {
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.add('hidden');
      this.emptyMessageTarget.style.display = 'none';
    }
  }
}